// Branded PDF export helper.
// Layout: company header (logo + name + legal info) + report title + autoTable bodies.
// Uses jsPDF + jspdf-autotable. Fetches /api/company and /api/files/{logo} for branding.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { api, tokenStore } from "./api";

const fetchLogoDataUrl = async (fileId) => {
  if (!fileId) return null;
  try {
    const token = tokenStore.get();
    const url = `${api.defaults.baseURL}/files/${fileId}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return null;
    const blob = await r.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
};

const fmtRoDate = (d) => {
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d || "";
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yy = dt.getFullYear();
    const HH = String(dt.getHours()).padStart(2, "0");
    const MM = String(dt.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yy} ${HH}:${MM}`;
  } catch { return d || ""; }
};

/**
 * Generate a branded PDF.
 * @param {object} opts
 *   title: string
 *   subtitle?: string
 *   sections: Array<{ heading?: string, summary?: Array<[label, value]>, table?: { columns, rows } }>
 *   filename: string
 */
export async function exportBrandedPDF(opts) {
  const { title, subtitle, sections = [], filename = "raport.pdf" } = opts;
  const company = (await api.get("/company").catch(() => ({ data: {} }))).data || {};
  const logoUrl = await fetchLogoDataUrl(company.logo_file_id);

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  let y = 12;

  // === Header band ===
  doc.setFillColor(11, 17, 32); // dark band
  doc.rect(0, 0, pageW, 28, "F");

  if (logoUrl) {
    try { doc.addImage(logoUrl, 12, 4, 20, 20); } catch {}
  }

  const headerX = logoUrl ? 36 : 12;
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold").setFontSize(14);
  doc.text(company.name || "ConVic", headerX, 10);
  doc.setFont("helvetica", "normal").setFontSize(8);
  if (company.legal_name) doc.text(company.legal_name, headerX, 14);
  const meta = [];
  if (company.tax_id) meta.push(`Cod fiscal: ${company.tax_id}`);
  if (company.vat_id) meta.push(`TVA: ${company.vat_id}`);
  if (meta.length) doc.text(meta.join("  ·  "), headerX, 18);
  if (company.address) doc.text(company.address, headerX, 22);
  const right = [];
  if (company.phone) right.push(`Tel: ${company.phone}`);
  if (company.email) right.push(company.email);
  if (company.website) right.push(company.website);
  if (right.length) {
    doc.setFontSize(7);
    right.forEach((line, i) => doc.text(line, pageW - 12, 10 + i * 4, { align: "right" }));
  }

  doc.setTextColor(0, 0, 0);
  y = 36;

  // === Title ===
  doc.setFont("helvetica", "bold").setFontSize(16);
  doc.text(title, 12, y);
  y += 6;
  if (subtitle) {
    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(120);
    doc.text(subtitle, 12, y);
    doc.setTextColor(0);
    y += 5;
  }
  doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(140);
  doc.text(`Generat: ${fmtRoDate(new Date())}`, pageW - 12, y, { align: "right" });
  doc.setTextColor(0);
  y += 4;

  // === Optional report header from settings ===
  if (company.report_header) {
    doc.setFontSize(9).setFont("helvetica", "italic").setTextColor(80);
    doc.text(company.report_header, 12, y);
    doc.setTextColor(0).setFont("helvetica", "normal");
    y += 5;
  }

  // === Sections ===
  sections.forEach((sec) => {
    if (sec.heading) {
      y += 2;
      doc.setFont("helvetica", "bold").setFontSize(11);
      doc.text(sec.heading, 12, y);
      y += 4;
    }
    if (sec.summary && sec.summary.length) {
      doc.setFont("helvetica", "normal").setFontSize(10);
      sec.summary.forEach(([label, value]) => {
        doc.setTextColor(110); doc.text(`${label}:`, 14, y);
        doc.setTextColor(0); doc.text(String(value), 60, y);
        y += 5;
      });
      y += 1;
    }
    if (sec.table) {
      autoTable(doc, {
        startY: y,
        head: [sec.table.columns],
        body: sec.table.rows,
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [0, 68, 255], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 246, 248] },
        margin: { left: 12, right: 12 },
      });
      y = (doc.lastAutoTable?.finalY || y) + 8;
    }
  });

  // === Footer ===
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal").setFontSize(7).setTextColor(150);
    const footerY = doc.internal.pageSize.getHeight() - 8;
    if (company.iban) doc.text(`IBAN: ${company.iban}${company.bank ? " · " + company.bank : ""}`, 12, footerY);
    doc.text(`Pagina ${i} / ${totalPages}`, pageW - 12, footerY, { align: "right" });
  }

  doc.save(filename);
}

/**
 * Generate a multi-page payslip PDF (one page per employee).
 * Each page has a compact branded header, employee identity, breakdown
 * (ore × tarif + diurnă), itemized deductions, and bold Net.
 *
 * @param {object} opts
 *   month: "YYYY-MM" (label generated from this)
 *   monthLabel: human-readable month (ex: "februarie 2026")
 *   payslips: Array<{
 *     person_name: string,
 *     person_firma?: string,
 *     hours: number, days_worked: number,
 *     hourly_rate?: number,
 *     salary_hours: number, diurna: number, salary_brut: number,
 *     deductions: Array<{ date: string, code: string, label: string, reason?: string, amount: number }>,
 *     total_deductions: number,
 *     salary_net: number,
 *   }>
 *   filename?: string
 */
export async function exportPayslipsPDF(opts) {
  const { month, monthLabel, payslips = [], filename = `fluturasi-${month}.pdf` } = opts;
  const company = (await api.get("/company").catch(() => ({ data: {} }))).data || {};
  const logoUrl = await fetchLogoDataUrl(company.logo_file_id);

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const drawHeader = () => {
    doc.setFillColor(11, 17, 32);
    doc.rect(0, 0, pageW, 24, "F");
    if (logoUrl) {
      try { doc.addImage(logoUrl, 10, 3, 18, 18); } catch {}
    }
    const hx = logoUrl ? 32 : 10;
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold").setFontSize(13);
    doc.text(company.name || "ConVic", hx, 9);
    doc.setFont("helvetica", "normal").setFontSize(7.5);
    if (company.legal_name) doc.text(company.legal_name, hx, 13);
    const meta = [];
    if (company.tax_id) meta.push(`Cod fiscal: ${company.tax_id}`);
    if (company.address) meta.push(company.address);
    if (meta.length) doc.text(meta.join("  ·  "), hx, 17);
    doc.setFont("helvetica", "bold").setFontSize(10);
    doc.text(`FLUTURAȘ SALARIU · ${monthLabel.toUpperCase()}`, pageW - 10, 14, { align: "right" });
    doc.setTextColor(0, 0, 0);
  };

  const drawFooter = (pageIdx, totalPages) => {
    doc.setFont("helvetica", "normal").setFontSize(7).setTextColor(150);
    const fy = pageH - 8;
    if (company.iban) doc.text(`IBAN: ${company.iban}${company.bank ? " · " + company.bank : ""}`, 10, fy);
    doc.text(`Pagina ${pageIdx} / ${totalPages}`, pageW - 10, fy, { align: "right" });
    doc.setTextColor(0);
  };

  payslips.forEach((p, idx) => {
    if (idx > 0) doc.addPage();
    drawHeader();
    let y = 32;

    // Identity block
    doc.setFont("helvetica", "bold").setFontSize(15).setTextColor(0);
    doc.text(p.person_name || "—", 10, y);
    y += 6;
    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(110);
    if (p.person_firma) {
      doc.text(`Firma angajatoare: ${p.person_firma}`, 10, y);
      y += 4;
    }
    doc.text(`Perioada: ${monthLabel}`, 10, y);
    y += 4;
    doc.text(`Generat: ${fmtRoDate(new Date())}`, 10, y);
    y += 6;
    doc.setTextColor(0);

    // Hours/brut table
    autoTable(doc, {
      startY: y,
      head: [["Element", "Cantitate", "Tarif", "Sumă (CHF)"]],
      body: [
        ["Ore lucrate", `${p.hours.toFixed(1)} h`, p.hourly_rate ? `${p.hourly_rate.toFixed(2)} CHF/h` : "—", p.salary_hours.toFixed(2)],
        ["Diurnă", `${p.days_worked} zile`, "16.00 CHF/zi", p.diurna.toFixed(2)],
        [{ content: "BRUT", styles: { fontStyle: "bold" } }, "", "", { content: p.salary_brut.toFixed(2), styles: { fontStyle: "bold", fillColor: [240, 245, 255] } }],
      ],
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [0, 68, 255], textColor: 255, fontStyle: "bold" },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
      margin: { left: 10, right: 10 },
    });
    y = (doc.lastAutoTable?.finalY || y) + 6;

    // Deductions table
    if ((p.deductions || []).length > 0) {
      doc.setFont("helvetica", "bold").setFontSize(11);
      doc.text("Deduceri", 10, y);
      y += 1;
      autoTable(doc, {
        startY: y + 2,
        head: [["Data", "Subcategorie", "Motiv", "Sumă (CHF)"]],
        body: [
          ...p.deductions.map((d) => [
            d.date || "—",
            d.label || d.code,
            d.reason || "—",
            `−${d.amount.toFixed(2)}`,
          ]),
          [{ content: "TOTAL DEDUCERI", colSpan: 3, styles: { fontStyle: "bold", halign: "right" } }, { content: `−${p.total_deductions.toFixed(2)}`, styles: { fontStyle: "bold", textColor: [180, 30, 30], fillColor: [255, 240, 240] } }],
        ],
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [180, 30, 30], textColor: 255, fontStyle: "bold" },
        columnStyles: { 3: { halign: "right" } },
        margin: { left: 10, right: 10 },
      });
      y = (doc.lastAutoTable?.finalY || y) + 6;
    } else {
      doc.setFont("helvetica", "italic").setFontSize(9).setTextColor(120);
      doc.text("Fără deduceri în această lună.", 10, y);
      doc.setTextColor(0);
      y += 6;
    }

    // Net result — large and prominent
    doc.setFillColor(220, 247, 220);
    doc.rect(10, y, pageW - 20, 16, "F");
    doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(0);
    doc.text("SALARIU NET DE PLATĂ", 14, y + 7);
    doc.setFontSize(16).setTextColor(20, 130, 50);
    doc.text(`${p.salary_net.toFixed(2)} CHF`, pageW - 14, y + 11, { align: "right" });
    doc.setTextColor(0);
    y += 22;

    // Formula footer
    doc.setFont("helvetica", "italic").setFontSize(7.5).setTextColor(120);
    doc.text("Calcul: Net = (Ore × tarif) + Diurnă (16 CHF/zi) − Deduceri aprobate", 10, y);

    // Signature line
    y = pageH - 30;
    doc.setDrawColor(160);
    doc.line(10, y, 90, y);
    doc.line(pageW - 90, y, pageW - 10, y);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(110);
    doc.text("Semnătură angajator", 10, y + 4);
    doc.text("Semnătură angajat", pageW - 10, y + 4, { align: "right" });
    doc.setTextColor(0);
  });

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(i, totalPages);
  }

  doc.save(filename);
}
